import clsx from "clsx";
import { FC, useEffect } from "react";

import { OutcomeChipList } from "../outcomes/OutcomeChipList";

import { StudentOutcomeChipList } from "./StudentOutcomeChipList";

import { OUTCOME_ID_QUERY_PARAM } from "components/constants";
import { OutcomeInfo } from "components/outcomes/OutcomeInfo";
import { displayOutcomeName } from "components/outcomes/helpers";
import { t } from "i18n/i18n";
import { ListItem } from "mds/components/ListItem";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useHomeRole } from "providers/HomeRoleProvider";
import { useHomeRoleCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCurrentUserId,
  selectOrgOutcomeRubric,
  selectOutcomesForCurrentUser,
} from "store/selectors";
import { useFindItemBySearchParamId } from "utils/hooks/use-find-item-by-search-param-id";
import { homeOutcomesUrl } from "utils/urls";

export const HomeOutcomesList: FC = () => {
  const homeRole = useHomeRole();
  const isSmallScreen = !useIsMdOrLarger();
  const { canAuthorSection } = useHomeRoleCapabilities();
  const currentUserId = useAppSelector(selectCurrentUserId);
  const outcomes = useAppSelector((s) => selectOutcomesForCurrentUser(s, homeRole));
  const currentOutcome = useFindItemBySearchParamId(outcomes, OUTCOME_ID_QUERY_PARAM);
  const outcomeId = currentOutcome?.id;

  const rubric = useAppSelector((s) => selectOrgOutcomeRubric(s, currentOutcome?.id));

  useEffect(() => {
    storeApi.outcomes.list();
    storeApi.orgs.list();
  }, []);

  if (!homeRole) {
    return null;
  }

  return (
    <div className={clsx("flex h-full", isSmallScreen && "flex-col")}>
      {isSmallScreen ? (
        <div>
          {canAuthorSection ? (
            <OutcomeChipList
              getOutcomeUrl={(outcome) => homeOutcomesUrl(homeRole, outcome.id)}
              outcomes={outcomes}
              selectedOutcomeId={outcomeId}
            />
          ) : (
            <StudentOutcomeChipList
              currentUserId={currentUserId}
              outcomes={outcomes}
              selectedOutcomeId={outcomeId}
            />
          )}
        </div>
      ) : (
        <div className="h-full w-full overflow-y-auto border-r border-black-tint-95 bg-white px-4 py-3 md:w-auto md:basis-[240px]">
          <h3 className="my-2">{t("glossary.outcomes")}</h3>
          {outcomes.length === 0 && (
            <div className="body-s w-full py-2 text-black-tint-40">
              {t("outcomes.no_org_outcomes")}
            </div>
          )}
          {outcomes.map((outcome) => (
            <ListItem
              className="w-full pl-0"
              isSelected={outcome.id === outcomeId}
              key={outcome.title}
              to={homeOutcomesUrl(homeRole, outcome.id)}
            >
              <div
                aria-label={`${t("glossary.outcome")} ${displayOutcomeName(outcome)}`}
                className="body-s my-1 w-full cursor-pointer truncate px-2"
                role="listitem"
              >
                {displayOutcomeName(outcome)}
              </div>
            </ListItem>
          ))}
        </div>
      )}

      <main className="h-full w-full">
        <OutcomeInfo isSectionLevel={false} outcomeItem={currentOutcome} rubric={rubric} readOnly />
      </main>

      {outcomeId && !currentOutcome && <div className="page">{t("error.404_message")}</div>}
    </div>
  );
};
